import { MealTime } from "@/clients/models/MealTime";
import { mealTabs, mealTabsLabel } from "@/constants/meal-tab";
import { cn } from "@/utils/cn";
import { memo, useMemo } from "react";

interface MealProgressBarProps {
  activeMealTime: MealTime;
}

const MealProgressBar = memo(function MealProgressBar({
  activeMealTime,
}: MealProgressBarProps) {
  const activeStepIndex = mealTabs.findIndex((step) => step === activeMealTime);

  const progressRatio = useMemo(() => {
    if (activeStepIndex === 0) {
      return 0;
    }
    if (activeStepIndex === mealTabs.length - 1) {
      return 1;
    }
    return (activeStepIndex + 0.5) / mealTabs.length;
  }, [activeStepIndex]);

  return (
    <div className="w-full h-16 flex flex-col gap-2">
      <div className="relative flex justify-between mx-2">
        <div
          className={cn(
            "absolute top-1/2 left-0 right-0 h-0.5 -translate-y-1/2 -z-10",
            "flex justify-start bg-gray-100 rounded-full overflow-hidden",
          )}
        >
          <div
            style={{
              width: `${progressRatio * 100}%`,
            }}
            className="h-full bg-primary-default"
          />
        </div>
        {mealTabs.map((step, index) => {
          const isActive = step === activeMealTime;
          const isCompleted = index < activeStepIndex;
          return (
            <div key={step} className="w-8 h-8 flex items-center">
              <div
                key={step}
                className={cn("w-8 h-8 rounded-full bg-white", {
                  "border-2 border-gray-500 bg-gray-200":
                    !isActive && isCompleted,
                  "bg-primary-default": isActive,
                  "border-gray-300 border-2 bg-white":
                    !isCompleted && !isActive,
                })}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between">
        {mealTabs.map((step, index) => {
          const isActive = step === activeMealTime;
          const isCompleted = index < activeStepIndex;

          return (
            <span
              className={cn("w-12 text-xs font-medium text-center", {
                "text-primary-default": isActive,
                "text-gray-500": isCompleted,
              })}
              key={step}
            >
              {mealTabsLabel[step]}
            </span>
          );
        })}
      </div>
    </div>
  );
});

export default MealProgressBar;
